import {
  AloftArea,
  AloftPrice,
  CraftsmanAdjustmentProps,
  ORDER_FEATURES,
  getCraftsmanAdjustment,
  isDefined,
  isUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectCompSequence } from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { selectDepreciationRatiosForFeature } from '../selectDepreciationRatioForFeature.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectAdjustmentProps } from './selectAdjustmentProps.ts';

const calculatePatioAdj = (
  adjustmentProps: CraftsmanAdjustmentProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  depreciationRatios: ReturnType<typeof selectDepreciationRatiosForFeature>,
  compID: string,
) => {
  const subjectImprovements = improvements.subject;
  const compImprovements = improvements[compID];

  const subjectPropertyIdentification = propertyIdentification.subject;
  const compPropertyIdentification = propertyIdentification[compID];

  if (
    isUndefined(subjectImprovements) ||
    isUndefined(compImprovements) ||
    isUndefined(depreciationRatios) ||
    isUndefined(subjectPropertyIdentification) ||
    isUndefined(compPropertyIdentification)
  ) {
    return undefined;
  }

  const depreciationRatio = depreciationRatios.subject;
  const compDepreciationRatio = depreciationRatios[compID];

  if (isUndefined(depreciationRatio) || isUndefined(compDepreciationRatio)) {
    return undefined;
  }

  const { patio, craftsmanQuality: quality, propertyType } = subjectImprovements;
  const { patio: compPatio, craftsmanQuality: compQuality } = compImprovements;
  const {
    address: { PostalCode: zipCode },
  } = subjectPropertyIdentification;
  const {
    address: { PostalCode: compZipCode },
  } = compPropertyIdentification;

  if (isUndefined(zipCode) || isUndefined(compZipCode)) {
    return undefined;
  }

  if (
    isUndefined(zipCode) ||
    isUndefined(compZipCode) ||
    isUndefined(propertyType) ||
    isUndefined(quality) ||
    isUndefined(compQuality) ||
    quality < 1
  ) {
    return undefined;
  }

  const adjCalculator = getCraftsmanAdjustment(adjustmentProps);

  if (patio?.type === undefined && compPatio?.type === undefined) {
    return undefined;
  }

  return adjCalculator.getPatioAdj({
    subject: {
      type: patio?.type,
      hasFeature: ORDER_FEATURES.patio.hasFeature(subjectImprovements),
      sqft: patio?.area ? AloftArea.schema.parse(patio?.area) : undefined,
      quality: patio?.quality ?? quality,
      propertyType,
      zipCode,
    },
    comp: {
      type: compPatio?.type,
      hasFeature: ORDER_FEATURES.patio.hasFeature(compImprovements),
      sqft: compPatio?.area ? AloftArea.schema.parse(compPatio?.area) : undefined,
      quality: compPatio?.quality ?? compQuality,
      propertyType,
      zipCode: compZipCode,
    },
    depreciationRatio,
    compDepreciationRatio,
  }).adjustment;
};

export const selectPatioAdj = createPriceSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectDepreciationRatiosForFeature(state, 'patio'),
    (state, id: string) => id,
  ],
  calculatePatioAdj,
);

export const selectPatioAdjs = createSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectDepreciationRatiosForFeature(state, 'patio'),
    selectCompSequence,
  ],
  (adjustmentVersion, improvements, propertyIdentification, depreciationRatios, compSequence) =>
    compSequence.reduce((acc: Record<string, AloftPrice>, propID) => {
      const adj = calculatePatioAdj(
        adjustmentVersion,
        improvements,
        propertyIdentification,
        depreciationRatios,
        propID,
      );

      if (isDefined(adj)) acc[propID] = adj;

      return acc;
    }, {}),
);
